import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, TextInput } from 'react-native';
import { DataStore, SortDirection } from 'aws-amplify';
import { UserGroup, UserType } from '../../models';
import { MaterialIcons } from '@expo/vector-icons';
import { DataTable } from 'react-native-paper';
import * as DataStoreUtil from '../../components/DataStoreUtil';
import * as AppConst from '../../components/AppConst';
import AddGroup from './AddGroup';
import GroupDetail from './GroupDetail';
import UpdateGroup from './UpdateGroup';

/** ================================
*  組織管理画面
*  ================================*/
const GroupList = () => {
  interface ManagedGroup {
    id: string;
    groupName: string;
    groupType: UserType | keyof typeof UserType;
  }

  // 全組織リスト
  const [groupList, setGroupList] = useState<ManagedGroup[]>([]);
  // 表示組織ユーザーリスト
  const [groupListPage, setGroupPage] = useState<ManagedGroup[]>([]);

  // 変更対象組織
  const [targetGroup, setTargetGroup] = useState<string>('');

  // 各種ダイアログ表示フラグ
  const [addGroup, setAddGroup] = useState<boolean>(false);
  const [detail, setDetail] = useState<boolean>(false);
  const [update, setUpdate] = useState<boolean>(false);

  // ページング
  const numberOfItemsPerPage = 10;
  const [page, setPage] = useState(0);

  // 検索ワード
  const [findWord, setFindWord] = useState('');

  useEffect(() => {
    // 最新情報取得
    updateUserGroup();

    // 購読開始
    const observeUserGroup = DataStore.observe(UserGroup).subscribe(updateUserGroup);

    // 画面破棄時に購読終了
    return () => {
      observeUserGroup.unsubscribe();
    }
  }, []);

  const updateUserGroup = async () => {
    const groups = (await DataStore.query(
      UserGroup,
      c => c.or(c => c.groupType('eq', UserType.PRODUCER).groupType('eq', UserType.INSTRUCTOR)).groupName('contains', findWord),
      {
        sort: s => s.createdAt(SortDirection.ASCENDING),
      }
    ));

    const tmpManagementGroup: ManagedGroup[] = [];
    for (const group of groups) {
      tmpManagementGroup.push({ id: group.id, groupName: group.groupName, groupType: group.groupType });
    }
    setGroupList([...tmpManagementGroup]);
  }

  // ページ切り替え時に表示切り替え
  useEffect(() => {
    setGroupPage([...(groupList.slice(page * numberOfItemsPerPage, (page + 1) * numberOfItemsPerPage))]);
  }, [page, groupList]);

  return (
    <View style={styles.container}>
      <View style={{ margin: 10 }}>
        {/* 新規登録 */}
        <TouchableOpacity style={styles.add} onPress={() => { setAddGroup(true); }}>
          <Text style={{ color: AppConst.defaultTextColor }}>＋ 新規登録</Text>
        </TouchableOpacity>

        {/* 検索窓 */}
        <View style={{ width: '100%', flexDirection: 'row', justifyContent: 'flex-end', marginTop: 30 }}>
          <TextInput
            style={{ borderBottomWidth: 1, width: 400, paddingLeft: 5 }}
            value={findWord}
            onChangeText={setFindWord}
            textContentType='none'
            autoCapitalize='none'
            placeholder='組織名で検索してリスト表示'
          />
          <TouchableOpacity style={{ borderBottomWidth: 1, width: 50, alignItems: 'center' }} onPress={() => { setPage(0); updateUserGroup(); }}>
            <MaterialIcons name='search' color={AppConst.defaultColor} size={24} />
          </TouchableOpacity>
        </View>

        {/* ユーザー一覧 */}
        <DataTable style={{ marginTop: 10 }}>
          <DataTable.Header style={{}}>
            <DataTable.Title style={{ flex: 2 }}>ID</DataTable.Title>
            <DataTable.Title style={{ flex: 2, paddingLeft: 20 }}>組織名</DataTable.Title>
            <DataTable.Title style={{ flex: 1, paddingLeft: 20 }}>タイプ</DataTable.Title>
            <DataTable.Title style={{ flex: 1, paddingLeft: 20 }}>操作</DataTable.Title>
          </DataTable.Header>

          {groupListPage.map((data: ManagedGroup, index: number) => (
            <DataTable.Row key={index} style={[{ minHeight: 35 }, index % 2 == 0 ? styles.lineEven : styles.lineOdd]}
              onPress={() => { setTargetGroup(data.id); setDetail(true); }}>
              <DataTable.Cell style={{ flex: 2 }}><Text style={AppConst.styles.fontSizeS}>{data.id}</Text></DataTable.Cell>
              <DataTable.Cell style={{ flex: 2, paddingLeft: 20 }}>{data.groupName}</DataTable.Cell>
              <DataTable.Cell style={{ flex: 1, paddingLeft: 20 }}>{DataStoreUtil.convUserType(data.groupType)}</DataTable.Cell>
              <DataTable.Cell style={{ flex: 1, paddingLeft: 20 }} onPress={() => { setTargetGroup(data.id); setUpdate(true); }}>
                <Text style={styles.control}>アカウント操作</Text>
              </DataTable.Cell>
            </DataTable.Row>
          ))}

          <DataTable.Pagination
            page={page}
            numberOfPages={Math.ceil(groupList.length / numberOfItemsPerPage)}
            onPageChange={(page) => setPage(page)}
            label={(page + 1) + 'ページ' + ' / ' + Math.ceil(groupList.length / numberOfItemsPerPage) + 'ページ'}
            showFastPaginationControls={true}
            numberOfItemsPerPage={numberOfItemsPerPage}
          />
        </DataTable>
      </View>

      {/* ユーザー登録 */}
      {addGroup && <AddGroup closeFunction={() => { setAddGroup(false) }} />}

      {/* ユーザー詳細 */}
      {detail && <GroupDetail closeFunction={() => { setDetail(false); setTargetGroup(''); }} groupId={targetGroup} />}

      {/* アカウント操作 */}
      {update && <UpdateGroup closeFunction={() => { setUpdate(false); setTargetGroup(''); }} groupId={targetGroup} />}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  add: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: AppConst.defaultColor,
    height: 30,
    width: '100%',
  },
  lineOdd: {
    backgroundColor: '#f4f4f4',
  },
  lineEven: {
    backgroundColor: '#f4ffff',
  },
  control: {
    textDecorationLine: 'underline',
    color: '#4b0082',
  },
});

export default GroupList;
